import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Countdown from "@/components/Countdown";
import { AlarmClock, X, ArrowRight } from "lucide-react";

export default function ReminderBanner() {
  const [info, setInfo] = useState(null); // {weekNum, cutoff, picked, total}
  const [dismissed, setDismissed] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (user?.role === "admin") return;
    let alive = true;
    (async () => {
      try {
        const { data: weeks } = await api.get("/weeks");
        const open = weeks.find((w) => !w.locked);
        if (!open) return;
        if (sessionStorage.getItem(`reminder_dismissed_${open.week_number}`)) {
          setDismissed(true);
        }
        const [wk, mine] = await Promise.all([
          api.get(`/weeks/${open.week_number}`),
          api.get(`/picks/${open.week_number}/me`),
        ]);
        if (!alive) return;
        const total = wk.data.games.length;
        const picked = Object.keys(mine.data.selections || {}).length;
        setInfo({ weekNum: open.week_number, cutoff: open.cutoff, picked, total });
      } catch {
        /* ignore */
      }
    })();
    return () => { alive = false; };
  }, [user]);

  if (!info || dismissed) return null;
  if (info.picked >= info.total) return null; // all picks in
  if (location.pathname === "/picks") return null; // already there

  const close = () => {
    sessionStorage.setItem(`reminder_dismissed_${info.weekNum}`, "1");
    setDismissed(true);
  };

  return (
    <div className="bg-[#E31837] text-white" data-testid="reminder-banner">
      <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center gap-3">
        <AlarmClock className="w-4 h-4 shrink-0" />
        <div className="flex-1 text-sm flex flex-wrap items-center gap-x-2">
          <span className="font-semibold uppercase tracking-wide font-['Bebas_Neue'] text-lg leading-none">
            Week {info.weekNum} picks close in
          </span>
          <span className="bg-black/25 px-2 py-0.5 rounded-sm">
            <Countdown target={info.cutoff} />
          </span>
          <span className="text-white/90">
            You've locked in {info.picked}/{info.total}.
          </span>
        </div>
        <button
          onClick={() => navigate("/picks")}
          className="flex items-center gap-1 bg-black/25 hover:bg-black/40 px-3 py-1.5 text-xs uppercase tracking-wider font-semibold transition-colors duration-200 shrink-0"
          data-testid="reminder-goto-picks"
        >
          Finish Picks <ArrowRight className="w-3.5 h-3.5" />
        </button>
        <button onClick={close} className="text-white/70 hover:text-white transition-colors duration-200 shrink-0" data-testid="reminder-dismiss">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
